'use client';

import { useEffect, useState, type ComponentType } from 'react';

import { FileText } from 'lucide-react';

import type { AttachmentFile } from './worksheetPhoto';

type PdfDialogProps = {
  file: AttachmentFile;
  onClose: () => void;
};

const fileName = (file: AttachmentFile) => {
  if (file.label) return file.label;
  return file.kind === 'pdf' ? 'PDF' : 'File';
};

const chipClassName =
  'inline-flex h-9 max-w-full min-w-0 items-center gap-2 rounded-lg bg-white/[0.06] px-2.5 text-[13px] text-white/85 ring-1 ring-inset ring-white/10 transition-transform duration-150 ease-[cubic-bezier(0.2,0,0,1)] hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white active:scale-[0.96] motion-reduce:transition-none motion-reduce:active:scale-100';

export function AttachmentFileLink({ file }: { file: AttachmentFile }) {
  const [open, setOpen] = useState(false);
  const [PdfDialog, setPdfDialog] =
    useState<ComponentType<PdfDialogProps> | null>(null);
  const name = fileName(file);
  const action = file.kind === 'pdf' ? 'Open PDF' : 'Open file';
  const label = file.label ? `${action}: ${file.label}` : action;

  useEffect(() => {
    if (!open || PdfDialog) return;
    let cancelled = false;
    // pdf.js reads DOMMatrix at import time, which the server does not have.
    import('./AttachmentPdfDialog').then(mod => {
      if (!cancelled) setPdfDialog(() => mod.AttachmentPdfDialog);
    });
    return () => {
      cancelled = true;
    };
  }, [open, PdfDialog]);

  if (file.kind === 'pdf') {
    return (
      <>
        <button
          type='button'
          data-cy='worksheet-attachment-file'
          data-kind={file.kind}
          aria-label={label}
          title={name}
          onClick={() => setOpen(true)}
          className={chipClassName}
        >
          <FileText size={16} strokeWidth={1.5} className='shrink-0' />
          <span className='truncate'>{name}</span>
        </button>
        {open && PdfDialog ? (
          <PdfDialog file={file} onClose={() => setOpen(false)} />
        ) : null}
      </>
    );
  }

  return (
    <a
      href={file.url}
      target='_blank'
      rel='noopener noreferrer'
      data-cy='worksheet-attachment-file'
      data-kind={file.kind}
      aria-label={label}
      title={name}
      className={chipClassName}
    >
      <FileText size={16} strokeWidth={1.5} className='shrink-0' />
      <span className='truncate'>{name}</span>
    </a>
  );
}
